import React, { useState, useEffect, useMemo } from "react";
import {
  TrendingUp,
  Plus,
  ArrowRightLeft,
} from "lucide-react";
import { usePageConfig } from "../../contexts/PageConfigContext";
import { ProductionRecord, Order, ProductSummary } from "../../types";
import { productionApi, ordersApi } from "../../services/api";
import { TodayProductionList } from "./components/TodayProductionList";
import { AddProductionForm } from "./components/AddProductionForm";
import { OrderDetailsDialog } from "./components/OrderDetailsDialog";
import { BulkTransferDialog } from "./components/BulkTransferDialog";
import { ProductComparisonView } from "./components/ProductComparisonView";
import { Dialog } from "../../components/Dialog";
import { Button } from "../../components/ui/button";
import { usePermission } from "../../hooks/usePermission";
import { Permissions } from "../../constants/permissions";
import {
  DateFilter,
  DateFilterValue,
  DateRange,
} from "../orders/components/DateFilter";

export const ProductionDashboard: React.FC = () => {
  const { setPageConfig } = usePageConfig();
  const [selectedPreset, setSelectedPreset] =
    useState<DateFilterValue>("today");
  const [customRange, setCustomRange] = useState<DateRange | undefined>(
    undefined,
  );
  const [productionRecords, setProductionRecords] = useState<
    ProductionRecord[]
  >([]);
  const [orders, setOrders] = useState<Order[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showBulkTransfer, setShowBulkTransfer] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<{
    productId: string;
    productName: string;
  } | null>(null);

  const canCreate = usePermission(Permissions.Production?.Create);

  // Calculate date range based on preset
  const getDateRange = (
    preset: DateFilterValue,
  ): { from: string; to: string } => {
    const today = new Date();

    switch (preset) {
      case "day-before-yesterday": {
        const d = new Date(today);
        d.setDate(d.getDate() - 2);
        return {
          from: d.toISOString().split("T")[0],
          to: d.toISOString().split("T")[0],
        };
      }
      case "yesterday": {
        const d = new Date(today);
        d.setDate(d.getDate() - 1);
        return {
          from: d.toISOString().split("T")[0],
          to: d.toISOString().split("T")[0],
        };
      }
      case "today":
        return {
          from: today.toISOString().split("T")[0],
          to: today.toISOString().split("T")[0],
        };
      case "tomorrow": {
        const d = new Date(today);
        d.setDate(d.getDate() + 1);
        return {
          from: d.toISOString().split("T")[0],
          to: d.toISOString().split("T")[0],
        };
      }
      case "day-after-tomorrow": {
        const d = new Date(today);
        d.setDate(d.getDate() + 2);
        return {
          from: d.toISOString().split("T")[0],
          to: d.toISOString().split("T")[0],
        };
      }
      case "this-week": {
        const weekStart = new Date(today);
        const dow = today.getDay() === 0 ? 7 : today.getDay();
        weekStart.setDate(today.getDate() - dow + 1); // Monday
        const weekEnd = new Date(weekStart);
        weekEnd.setDate(weekStart.getDate() + 6); // Sunday
        return {
          from: weekStart.toISOString().split("T")[0],
          to: weekEnd.toISOString().split("T")[0],
        };
      }
      case "this-month": {
        const monthEnd = new Date(today.getFullYear(), today.getMonth() + 1, 0);
        return {
          from: `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-01`,
          to: monthEnd.toISOString().split("T")[0],
        };
      }
      case "custom":
        return {
          from: customRange?.from ?? "",
          to: customRange?.to ?? "",
        };
      case "all":
      default:
        return { from: "", to: "" };
    }
  };

  useEffect(() => {
    const actions = [];

    if (canCreate && selectedPreset === "today") {
      actions.push({
        label: "Transferir Pendientes",
        onClick: () => setShowBulkTransfer(true),
        variant: "secondary" as const,
        icon: ArrowRightLeft,
        iconOnlyOnTablet: true,
      });
    }

    if (canCreate) {
      actions.push({
        label: "Añadir Producción",
        onClick: () => setShowAddForm(true),
        variant: "primary" as const,
        icon: Plus,
        iconOnlyOnTablet: true,
      });
    }

    setPageConfig({
      title: "Producción",
      description: "Dashboard de producción y comandas",
      headerComponent: (
        <DateFilter
          value={selectedPreset}
          customRange={customRange}
          onChange={(value, range) => {
            setSelectedPreset(value);
            setCustomRange(range);
          }}
        />
      ),
      actions,
    });
  }, [setPageConfig, canCreate, selectedPreset, customRange]);

  // Load data when date range changes
  useEffect(() => {
    loadData();
  }, [selectedPreset, customRange]);

  const loadData = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const range = getDateRange(selectedPreset);
      const fromParam = range.from || undefined;
      const toParam = range.to || undefined;

      // Load production records and orders in parallel
      const [productionData, ordersData] = await Promise.all([
        productionApi.getRecords({ from: fromParam, to: toParam }),
        ordersApi.getAll({ dateFrom: fromParam, dateTo: toParam }),
      ]);

      setProductionRecords(productionData as any);
      setOrders(ordersData as any);
    } catch (err: any) {
      console.error("Error loading data:", err);
      setError(err.message || "Error al cargar los datos");
    } finally {
      setIsLoading(false);
    }
  };

  // Calculate production summary
  const productionSummary: ProductSummary[] = productionRecords
    .reduce((acc, record) => {
      const existing = acc.find((item) => item.productId === record.productId);

      if (existing) {
        existing.recordCount++;
        const unitEntry = existing.quantities.find(
          (q) => q.unit === record.unit,
        );
        if (unitEntry) {
          unitEntry.quantity += record.quantity;
        } else {
          existing.quantities.push({
            quantity: record.quantity,
            unit: record.unit,
          });
        }
      } else {
        acc.push({
          productId: record.productId,
          productName: record.productName,
          quantities: [
            {
              quantity: record.quantity,
              unit: record.unit,
            },
          ],
          recordCount: 1,
        });
      }

      return acc;
    }, [] as ProductSummary[])
    .sort((a, b) => {
      // Sort alphabetically by productName (case-insensitive)
      const nameCompare = (a.productName || "").localeCompare(
        b.productName || "",
        "es",
        { sensitivity: "base" },
      );
      if (nameCompare !== 0) return nameCompare;
      // If names are equal, sort by productId
      return (a.productId || "").localeCompare(b.productId || "");
    });

  // Calculate orders summary
  const ordersSummary: ProductSummary[] = orders
    .reduce((acc, order) => {
      order.lines.forEach((line) => {
        const existing = acc.find((item) => item.productId === line.productId);

        if (existing) {
          existing.recordCount++;
          const unitEntry = existing.quantities.find(
            (q) => q.unit === line.unit,
          );
          if (unitEntry) {
            unitEntry.quantity += line.quantity;
          } else {
            existing.quantities.push({
              quantity: line.quantity,
              unit: line.unit,
            });
          }
        } else {
          acc.push({
            productId: line.productId,
            productName: line.productName,
            quantities: [
              {
                quantity: line.quantity,
                unit: line.unit,
              },
            ],
            recordCount: 1,
          });
        }
      });

      return acc;
    }, [] as ProductSummary[])
    .sort((a, b) => {
      // Sort alphabetically by productName (case-insensitive)
      const nameCompare = (a.productName || "").localeCompare(
        b.productName || "",
        "es",
        { sensitivity: "base" },
      );
      if (nameCompare !== 0) return nameCompare;
      // If names are equal, sort by productId
      return (a.productId || "").localeCompare(b.productId || "");
    });

  // Calculate pending items for bulk transfer (only for today)
  const pendingItems = useMemo(() => {
    if (selectedPreset !== "today") return [];

    const today = new Date().toISOString().split("T")[0];
    const todayRecords = productionRecords.filter((r) =>
      r.producedAt.startsWith(today),
    );
    const todayOrders = orders.filter((o) => o.orderDate.startsWith(today));

    // Group production by product/unit
    const productionMap = new Map<
      string,
      { productId: string; productName: string; unit: string; total: number }
    >();
    todayRecords.forEach((record) => {
      const key = `${record.productId}-${record.unit}`;
      const existing = productionMap.get(key);
      if (existing) {
        existing.total += record.quantity;
      } else {
        productionMap.set(key, {
          productId: record.productId,
          productName: record.productName,
          unit: record.unit,
          total: record.quantity,
        });
      }
    });

    // Subtract orders
    todayOrders.forEach((order) => {
      order.lines.forEach((line) => {
        const key = `${line.productId}-${line.unit}`;
        const existing = productionMap.get(key);
        if (existing) {
          existing.total -= line.quantity;
        }
      });
    });

    // Filter only items with pending quantity > 0
    return Array.from(productionMap.values())
      .filter((item) => item.total > 0)
      .map((item) => ({
        productId: item.productId,
        productName: item.productName,
        unit: item.unit,
        availableQuantity: item.total,
        transferQuantity: item.total, // Default to transfer all pending
      }))
      .sort((a, b) =>
        a.productName.localeCompare(b.productName, "es", {
          sensitivity: "base",
        }),
      );
  }, [productionRecords, orders, selectedPreset]);

  // Helper to get section title
  const getSectionTitle = () => {
    switch (selectedPreset) {
      case "today":
        return "Producción de Hoy";
      case "yesterday":
        return "Producción de Ayer";
      case "this-week":
        return "Producción de Esta Semana";
      case "this-month":
        return "Producción de Este Mes";
      case "custom":
        return "Histórico de Producción";
      default:
        return "Registro de Producción";
    }
  };

  const handleProductionAdded = () => {
    setShowAddForm(false);
    loadData();
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-green-600 mb-4"></div>
          <p className="text-slate-600">Cargando datos...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <TrendingUp className="mx-auto mb-4 text-red-500" size={48} />
          <p className="text-red-600 font-medium mb-2">
            Error al cargar los datos
          </p>
          <p className="text-slate-600 text-sm">{error}</p>
          <button
            onClick={loadData}
            className="mt-4 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
          >
            Reintentar
          </button>
        </div>
      </div>
    );
  }

  const range = getDateRange(selectedPreset);

  return (
    <div className="w-full space-y-6">
      <div className="flex flex-row flex-wrap items-start gap-6">
        {/* Comparison View */}
        <div className="flex-1 basis-[320px] min-w-0">
          <ProductComparisonView
            productionSummary={productionSummary}
            ordersSummary={ordersSummary}
            onProductClick={(productId, productName) =>
              setSelectedProduct({ productId, productName })
            }
          />
        </div>

        {/* Production Records */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 flex-1 basis-[320px] min-w-0">
          <div className="w-full flex items-center justify-between p-4 md:p-6">
            <div className="text-left">
              <h3 className="text-base font-bold text-slate-900">
                {getSectionTitle()}
              </h3>
              <p className="text-xs text-slate-500 mt-0.5">
                {selectedPreset === "today"
                  ? new Date().toLocaleDateString("es-ES", {
                      weekday: "long",
                      day: "numeric",
                      month: "long",
                      year: "numeric",
                    })
                  : selectedPreset === "all" || !range.from || !range.to
                    ? "Todas las fechas"
                    : `Del ${new Date(range.from).toLocaleDateString("es-ES")} al ${new Date(range.to).toLocaleDateString("es-ES")}`}
              </p>
            </div>
            {canCreate && (
              <Button
                onClick={() => setShowAddForm(true)}
                aria-label="Añadir Producción"
                title="Añadir Producción"
                size="icon"
                className="shrink-0"
              >
                <Plus size={18} />
              </Button>
            )}
          </div>

          <div className="px-4 pb-4 md:px-6 md:pb-6 border-t border-slate-100">
            <TodayProductionList records={productionRecords} onDelete={loadData} />
          </div>
        </div>
      </div>

      {/* Add Production Dialog */}
      <Dialog
        isOpen={showAddForm}
        onClose={() => setShowAddForm(false)}
        title="Registrar Producción"
        size="lg"
      >
        <AddProductionForm
          onSuccess={handleProductionAdded}
          onCancel={() => setShowAddForm(false)}
        />
      </Dialog>

      {/* Order Details Dialog */}
      {selectedProduct && (
        <OrderDetailsDialog
          isOpen={!!selectedProduct}
          onClose={() => setSelectedProduct(null)}
          productId={selectedProduct.productId}
          productName={selectedProduct.productName}
          orders={orders}
        />
      )}

      {/* Bulk Transfer Dialog */}
      <BulkTransferDialog
        isOpen={showBulkTransfer}
        onClose={() => setShowBulkTransfer(false)}
        sourceDate={new Date().toISOString().split("T")[0]}
        pendingItems={pendingItems}
        onSuccess={loadData}
      />
    </div>
  );
};
